import React, { useEffect, useRef, useState } from 'react'
import { Alert, ProgressBar } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useReportApi } from '../../../resources/hooks/api/useReportApiHook'

// Shows Balance Sheet refresh progress on the dashboard, so the user doesn't have to open
// the Balance Sheet page itself to know a refresh (e.g. after a QBD import/reconciliation)
// is still running. Purely polls GET /balance/sheets/refresh-status (read-only; does not
// trigger or affect generation in any way). Renders nothing when no refresh is in progress.
// Note: gl-api wraps responses, so the payload is at resp.data.data.

interface Props {
    branchId: number;
}

const POLL_MS = 5000

const BalanceSheetRefreshStatus: React.FC<Props> = ({ branchId }) => {
    const { t } = useTranslation("scenes.thirdparty.quickbook.Dashboard")
    const { balanceSheetRefreshStatus } = useReportApi()
    const [status, setStatus] = useState<any>(null)
    const interval = useRef<any>(null)

    useEffect(() => {
        if (!branchId) return

        const tick = () => {
            balanceSheetRefreshStatus({ branch_id: branchId }, (_msg: string, resp: any) => {
                const data = resp?.data?.data
                if (!data || !data.in_progress) {
                    setStatus(null)
                    return
                }
                setStatus(data)
            }, () => {
                // Silently ignore errors -- this is a best-effort status banner, not a
                // page-critical fetch, so don't surface noise on the dashboard for it.
            })
        }

        tick()
        interval.current = setInterval(tick, POLL_MS)
        return () => clearInterval(interval.current)
    }, [branchId])

    if (!status) return null

    const total = status.total_months || 0
    const completed = status.completed_months || 0
    const pct = total > 0 ? Math.min(100, Math.round((completed / total) * 100)) : 0

    return (
        <Alert variant="primary" className="mb-4 d-flex align-items-center gap-3">
            <span style={{
                flex: 'none', width: 36, height: 36, borderRadius: 10, background: '#9ec5fe',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 15,
            }}>
                <i className="fa fa-spinner fa-spin" aria-hidden="true"></i>
            </span>
            <div className="flex-grow-1" style={{ minWidth: 0 }}>
                <div className="fw-bold">{t('balanceSheetRefresh.title', 'Balance Sheet refresh in progress')}</div>
                <div className="small text-muted">
                    {t('balanceSheetRefresh.caveat', 'Some reports and the GL Dashboard may not work properly until it completes.')}
                </div>
                <div>
                    {total > 0
                        ? t('balanceSheetRefresh.progress', '{{completed}} of {{total}} months completed', { completed, total })
                        : t('balanceSheetRefresh.starting', 'Starting…')}
                </div>
                {total > 0 && (
                    <ProgressBar now={pct} label={`${pct}%`} style={{ height: 8 }} className="mt-1" />
                )}
            </div>
        </Alert>
    )
}

export default BalanceSheetRefreshStatus
